import type { ComponentProps, ReactNode } from 'react';

import { cn } from '@/lib/utils';

/**
 * The brand's signature shape: radius on the top corners only, marking a
 * section opener.
 *
 * `shape="dome"` is the hero's near-semicircle and expects a fixed aspect
 * ratio; `shape="arch"` is the shallower version for content-height cards.
 * Both are percentage radii defined in `LandingStyles` — see the note there
 * for why a px radius drifts between one card and the next.
 *
 * `overflow-hidden` is not optional: percentage vertical radii resolve against
 * the border box, so content that grows the box grows the arch with it.
 */
const toneClass = {
    paper: 'bg-card text-card-foreground',
    cream: 'bg-background text-foreground',
    oxblood: 'mn-on-oxblood bg-background text-foreground',
} as const;

export function ArchCard({
    tone,
    children,
    className,
    shape = 'arch',
    ...props
}: ComponentProps<'div'> & {
    shape?: 'arch' | 'dome';
    tone: keyof typeof toneClass;
    children: ReactNode;
}) {
    return (
        <div
            {...props}
            className={cn(
                shape === 'dome' ? 'mn-arch-dome' : 'mn-arch',
                'relative overflow-hidden',
                toneClass[tone],
                className,
            )}
        >
            {children}
        </div>
    );
}
